import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import Button from '../components/Button';
import api from '../services/api';

const WIDGET_REGISTRY = {
  provider: [
    { id: 'recently_viewed',        label: 'Recently Viewed',          defaultVisible: true, defaultPosition: 1 },
    { id: 'patient_counts',         label: 'My Patients',              defaultVisible: true, defaultPosition: 2 },
    { id: 'diagnosis_distribution', label: 'Diagnosis Distribution',   defaultVisible: true, defaultPosition: 3 },
    { id: 'high_risk',              label: 'High-Risk Patients',       defaultVisible: true, defaultPosition: 4 },
    { id: 'labs_pending',           label: 'Upcoming — Labs Pending',  defaultVisible: true, defaultPosition: 5 },
    { id: 'esa_compliance',         label: 'ESA Compliance',           defaultVisible: true, defaultPosition: 6 },
    { id: 'care_gaps',              label: 'Care Gap Alerts',          defaultVisible: true, defaultPosition: 7 },
    { id: 'access_referrals',       label: 'Access Referrals',         defaultVisible: true, defaultPosition: 8 },
    { id: 'transplant_referrals',   label: 'Transplant Referrals',     defaultVisible: true, defaultPosition: 9 },
  ],
  clinical_staff: [
    { id: 'patient_counts',         label: 'Practice Patients',        defaultVisible: true, defaultPosition: 1 },
    { id: 'recently_added',         label: 'Recently Added Patients',  defaultVisible: true, defaultPosition: 2 },
    { id: 'diagnosis_distribution', label: 'Diagnosis Distribution',   defaultVisible: true, defaultPosition: 3 },
    { id: 'labs_pending',           label: 'Upcoming — Labs Pending',  defaultVisible: true, defaultPosition: 4 },
    { id: 'care_gaps',              label: 'Care Gap Alerts',          defaultVisible: true, defaultPosition: 5 },
    { id: 'esa_injections',         label: 'ESA Injections Due',       defaultVisible: true, defaultPosition: 6 },
    { id: 'immunization_alerts',    label: 'Immunization Alerts',      defaultVisible: true, defaultPosition: 7 },
  ],
  non_clinical_staff: [
    { id: 'patient_counts',         label: 'Practice Population',      defaultVisible: true, defaultPosition: 1 },
    { id: 'diagnosis_distribution', label: 'Diagnosis Distribution',   defaultVisible: true, defaultPosition: 2 },
    { id: 'report_shortcuts',       label: 'Report Shortcuts',         defaultVisible: true, defaultPosition: 3 },
  ],
  admin: [
    { id: 'patient_counts',         label: 'Practice Population',      defaultVisible: true, defaultPosition: 1 },
    { id: 'user_summary',           label: 'User Accounts',            defaultVisible: true, defaultPosition: 2 },
    { id: 'diagnosis_distribution', label: 'Diagnosis Distribution',   defaultVisible: true, defaultPosition: 3 },
    { id: 'audit_summary',          label: 'System Activity (24h)',    defaultVisible: true, defaultPosition: 4 },
  ],
};

function timeAgo(isoString) {
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
}

// Widget hide button is an icon dismiss control — plain button intentional
function WidgetShell({ title, children, onHide }) {
  return (
    <div style={styles.widget}>
      <div style={styles.widgetHeader}>
        <h3 style={styles.widgetTitle}>{title}</h3>
        <button style={styles.hideBtn} onClick={onHide} title="Hide widget">×</button>
      </div>
      <div style={styles.widgetBody}>{children}</div>
    </div>
  );
}

function PlaceholderWidget({ title, stage, description, onHide }) {
  return (
    <WidgetShell title={title} onHide={onHide}>
      <div style={styles.placeholderBody}>
        <div style={styles.placeholderStage}>Coming in {stage}</div>
        {description && <div style={styles.placeholderDesc}>{description}</div>}
      </div>
    </WidgetShell>
  );
}

function RecentlyViewedWidget({ onHide }) {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/recently-viewed')
      .then(res => setPatients(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <WidgetShell title="Recently Viewed" onHide={onHide}>
      {loading ? <div style={styles.widgetLoading}>Loading...</div>
        : patients.length === 0 ? <div style={styles.widgetEmpty}>No recently viewed patients.</div>
        : (
          <div style={styles.patientList}>
            {patients.map(p => (
              // patientRow is a list-item navigation control — plain button intentional
              <button key={p.patient_id} style={styles.patientRow}
                onClick={() => navigate(`/patients/${p.patient_id}`)}>
                <div style={styles.patientRowLeft}>
                  <span style={styles.patientName}>{p.first_name} {p.last_name}</span>
                  <span style={styles.patientMrn}>MRN: {p.mrn}</span>
                </div>
                <span style={styles.timeAgo}>{timeAgo(p.viewed_at)}</span>
              </button>
            ))}
          </div>
        )}
    </WidgetShell>
  );
}

function PatientCountsWidget({ title, providerId, onHide }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = providerId ? `?provider_id=${providerId}` : '';
    api.get(`/dashboard/patient-counts${params}`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [providerId]);

  if (loading) return <WidgetShell title={title} onHide={onHide}><div style={styles.widgetLoading}>Loading...</div></WidgetShell>;
  if (!data) return null;

  const rows = [
    { label: 'Active CKD',  value: data.active,    color: '#065f46' },
    { label: 'Transplant',  value: data.transplant, color: '#1e40af' },
    { label: 'Dialysis',    value: data.dialysis,   color: '#1e40af' },
    { label: 'Hospice',     value: data.hospice,    color: 'var(--color-text-muted)' },
    { label: 'Inactive',    value: data.inactive,   color: '#92400e' },
    { label: 'Deceased',    value: data.deceased,   color: 'var(--color-text-muted)' },
  ].filter(r => r.value > 0);

  return (
    <WidgetShell title={title} onHide={onHide}>
      <div style={styles.countSummary}>
        <div style={styles.totalCount}>{data.clinically_active}</div>
        <div style={styles.totalLabel}>Clinically Active Patients</div>
      </div>
      <div style={styles.countRows}>
        {rows.map(row => (
          <div key={row.label} style={styles.countRow}>
            <span style={styles.countLabel}>{row.label}</span>
            <span style={{ ...styles.countValue, color: row.color }}>{row.value}</span>
          </div>
        ))}
      </div>
      <Button variant="ghost" onClick={() => navigate('/patients')}
        style={{ padding: '8px 0 0', justifyContent: 'flex-start' }}>
        View patient registry →
      </Button>
    </WidgetShell>
  );
}

function DiagnosisDistributionWidget({ title, providerId, onHide }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = providerId ? `?provider_id=${providerId}&limit=8` : '?limit=8';
    api.get(`/dashboard/diagnosis-distribution${params}`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [providerId]);

  if (loading) return <WidgetShell title={title} onHide={onHide}><div style={styles.widgetLoading}>Loading...</div></WidgetShell>;
  if (!data || data.total === 0) return (
    <WidgetShell title={title} onHide={onHide}>
      <div style={styles.widgetEmpty}>No diagnosis data available yet.</div>
    </WidgetShell>
  );

  const allRows = [
    ...data.diagnoses,
    ...(data.other_count > 0 ? [{
      display_name: 'Other', icd10_equivalent: null,
      count: data.other_count, percent: data.other_percent,
    }] : []),
  ];

  return (
    <WidgetShell title={title} onHide={onHide}>
      <div style={styles.dxDistList}>
        {allRows.map((dx, i) => (
          <div key={i} style={styles.dxDistRow}>
            <div style={styles.dxDistInfo}>
              <span style={styles.dxDistName}>{dx.display_name}</span>
              {dx.icd10_equivalent && <span style={styles.dxDistCode}>{dx.icd10_equivalent}</span>}
            </div>
            <div style={styles.dxDistRight}>
              <div style={styles.dxBar}>
                <div style={{ ...styles.dxBarFill, width: `${dx.percent}%` }} />
              </div>
              <span style={styles.dxDistCount}>
                {dx.count} <span style={styles.dxDistPercent}>({dx.percent}%)</span>
              </span>
            </div>
          </div>
        ))}
      </div>
      <div style={styles.dxDistTotal}>{data.total} active patients total</div>
    </WidgetShell>
  );
}

function RecentlyAddedWidget({ onHide }) {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/recently-added-patients?limit=10')
      .then(res => setPatients(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <WidgetShell title="Recently Added Patients" onHide={onHide}>
      {loading ? <div style={styles.widgetLoading}>Loading...</div>
        : patients.length === 0 ? <div style={styles.widgetEmpty}>No patients added yet.</div>
        : (
          <div style={styles.patientList}>
            {patients.map(p => (
              <button key={p.patient_id} style={styles.patientRow}
                onClick={() => navigate(`/patients/${p.patient_id}`)}>
                <div style={styles.patientRowLeft}>
                  <span style={styles.patientName}>{p.first_name} {p.last_name}</span>
                  <span style={styles.patientMrn}>MRN: {p.mrn}</span>
                </div>
                <span style={styles.timeAgo}>{timeAgo(p.created_at)}</span>
              </button>
            ))}
          </div>
        )}
    </WidgetShell>
  );
}

function UserSummaryWidget({ onHide }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const ROLE_LABELS = {
    provider: 'Providers', clinical_staff: 'Clinical Staff',
    non_clinical_staff: 'Non-Clinical Staff', admin: 'Admins',
  };

  useEffect(() => {
    api.get('/dashboard/user-summary')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <WidgetShell title="User Accounts" onHide={onHide}><div style={styles.widgetLoading}>Loading...</div></WidgetShell>;
  if (!data) return null;

  return (
    <WidgetShell title="User Accounts" onHide={onHide}>
      <div style={styles.countSummary}>
        <div style={styles.totalCount}>{data.active}</div>
        <div style={styles.totalLabel}>Active Users</div>
      </div>
      <div style={styles.countRows}>
        {Object.entries(data.by_role).map(([role, count]) => (
          <div key={role} style={styles.countRow}>
            <span style={styles.countLabel}>{ROLE_LABELS[role] || role}</span>
            <span style={styles.countValue}>{count}</span>
          </div>
        ))}
        {data.inactive > 0 && (
          <div style={styles.countRow}>
            <span style={{ ...styles.countLabel, color: 'var(--color-text-placeholder)' }}>Inactive</span>
            <span style={{ ...styles.countValue, color: 'var(--color-text-placeholder)' }}>{data.inactive}</span>
          </div>
        )}
      </div>
      <Button variant="ghost" onClick={() => navigate('/settings')}
        style={{ padding: '8px 0 0', justifyContent: 'flex-start' }}>
        Manage users →
      </Button>
    </WidgetShell>
  );
}

function AuditSummaryWidget({ onHide }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const ACTION_LABELS = {
    READ: 'Chart views', CREATE: 'Records created',
    UPDATE: 'Records updated', STATUS_CHANGE: 'Status changes', DELETE: 'Records deleted',
  };

  useEffect(() => {
    api.get('/dashboard/audit-summary')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <WidgetShell title="System Activity (24h)" onHide={onHide}><div style={styles.widgetLoading}>Loading...</div></WidgetShell>;
  if (!data) return null;

  return (
    <WidgetShell title="System Activity (24h)" onHide={onHide}>
      <div style={styles.countSummary}>
        <div style={styles.totalCount}>{data.total_events_24h}</div>
        <div style={styles.totalLabel}>Total Events</div>
      </div>
      <div style={styles.countRows}>
        {Object.entries(data.by_action).map(([action, count]) => (
          <div key={action} style={styles.countRow}>
            <span style={styles.countLabel}>{ACTION_LABELS[action] || action}</span>
            <span style={styles.countValue}>{count}</span>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

function ReportShortcutsWidget({ onHide }) {
  const navigate = useNavigate();
  return (
    <WidgetShell title="Report Shortcuts" onHide={onHide}>
      <div style={styles.shortcutList}>
        {[
          { label: 'Patient Registry', path: '/patients' },
          { label: 'Report Center', path: '/reports' },
        ].map(s => (
          <Button key={s.path} variant="secondary" onClick={() => navigate(s.path)}
            style={{ justifyContent: 'flex-start', width: '100%' }}>
            {s.label} →
          </Button>
        ))}
      </div>
    </WidgetShell>
  );
}

function HiddenWidgetsPanel({ hiddenWidgets, onRestore }) {
  if (hiddenWidgets.length === 0) return null;
  return (
    <div style={styles.hiddenPanel}>
      <span style={styles.hiddenLabel}>Hidden widgets:</span>
      {hiddenWidgets.map(w => (
        <Button key={w.id} variant="secondary" size="sm" onClick={() => onRestore(w.id)}>
          + {w.label}
        </Button>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  useInactivityTimer();
  const { user } = useAuth();
  const role = user?.role || 'provider';
  const [widgetPrefs, setWidgetPrefs] = useState({});
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  useEffect(() => {
    api.get('/dashboard/prefs')
      .then(res => {
        const prefs = {};
        res.data.forEach(p => { prefs[p.widget_id] = p; });
        setWidgetPrefs(prefs);
      })
      .catch(() => {})
      .finally(() => setPrefsLoaded(true));
  }, []);

  const roleWidgets = WIDGET_REGISTRY[role] || WIDGET_REGISTRY['provider'];

  const resolvedWidgets = roleWidgets.map(w => ({
    ...w,
    visible: widgetPrefs[w.id]?.visible ?? w.defaultVisible,
    position: widgetPrefs[w.id]?.position ?? w.defaultPosition,
  })).sort((a, b) => a.position - b.position);

  const visibleWidgets = resolvedWidgets.filter(w => w.visible);
  const hiddenWidgets = resolvedWidgets.filter(w => !w.visible);

  const hideWidget = async (widgetId) => {
    setWidgetPrefs(prev => ({ ...prev, [widgetId]: { ...prev[widgetId], visible: false } }));
    try { await api.post(`/dashboard/prefs/${widgetId}?visible=false`); } catch {}
  };

  const restoreWidget = async (widgetId) => {
    setWidgetPrefs(prev => ({ ...prev, [widgetId]: { ...prev[widgetId], visible: true } }));
    try { await api.post(`/dashboard/prefs/${widgetId}?visible=true`); } catch {}
  };

  if (!prefsLoaded) return <div style={styles.loading}>Loading dashboard...</div>;

  const providerId = role === 'provider' ? user?.id : null;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const renderWidget = (widget) => {
    const hide = () => hideWidget(widget.id);
    switch (widget.id) {
      case 'recently_viewed':        return <RecentlyViewedWidget key={widget.id} onHide={hide} />;
      case 'patient_counts':         return <PatientCountsWidget key={widget.id} title={widget.label} providerId={providerId} onHide={hide} />;
      case 'diagnosis_distribution': return <DiagnosisDistributionWidget key={widget.id} title={widget.label} providerId={providerId} onHide={hide} />;
      case 'recently_added':         return <RecentlyAddedWidget key={widget.id} onHide={hide} />;
      case 'user_summary':           return <UserSummaryWidget key={widget.id} onHide={hide} />;
      case 'audit_summary':          return <AuditSummaryWidget key={widget.id} onHide={hide} />;
      case 'report_shortcuts':       return <ReportShortcutsWidget key={widget.id} onHide={hide} />;
      case 'high_risk':              return <PlaceholderWidget key={widget.id} title="High-Risk Patients" stage="Stage 6" description="Patients with KFRE above threshold." onHide={hide} />;
      case 'labs_pending':           return <PlaceholderWidget key={widget.id} title="Upcoming — Labs Pending" stage="Stage 4" description="Patients with upcoming appointments and overdue labs." onHide={hide} />;
      case 'esa_compliance':         return <PlaceholderWidget key={widget.id} title="ESA Compliance" stage="Stage 9" description="Active ESA patients with overdue Hgb or injection." onHide={hide} />;
      case 'care_gaps':              return <PlaceholderWidget key={widget.id} title="Care Gap Alerts" stage="Stage 8" description="Patients missing CKD-protective medications." onHide={hide} />;
      case 'access_referrals':       return <PlaceholderWidget key={widget.id} title="Access Referrals" stage="Stage 12" description="Open dialysis access referrals." onHide={hide} />;
      case 'transplant_referrals':   return <PlaceholderWidget key={widget.id} title="Transplant Referrals" stage="Stage 13" description="Active waitlist and pending referrals." onHide={hide} />;
      case 'esa_injections':         return <PlaceholderWidget key={widget.id} title="ESA Injections Due" stage="Stage 9" description="Patients with ESA injections due today." onHide={hide} />;
      case 'immunization_alerts':    return <PlaceholderWidget key={widget.id} title="Immunization Alerts" stage="Stage 15" description="Patients with overdue or urgently needed vaccines." onHide={hide} />;
      default: return null;
    }
  };

  return (
    <div>
      <div style={styles.pageHeader}>
        <h1 style={styles.greeting}>{greeting()}, {user?.first_name}.</h1>
        <p style={styles.subGreeting}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      <HiddenWidgetsPanel hiddenWidgets={hiddenWidgets} onRestore={restoreWidget} />

      <div style={styles.widgetGrid}>
        {visibleWidgets.map(widget => renderWidget(widget))}
      </div>
    </div>
  );
}

const styles = {
  loading: { padding: '48px 24px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  pageHeader: { marginBottom: '20px' },
  greeting: { fontSize: '22px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: '0 0 4px', fontFamily: 'var(--font-family-base)' },
  subGreeting: { fontSize: '13px', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
  hiddenPanel: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    marginBottom: '16px', padding: '10px 14px',
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)',
  },
  hiddenLabel: { color: 'var(--color-text-muted)', marginRight: '4px', fontFamily: 'var(--font-family-base)' },
  widgetGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px', alignItems: 'start',
  },
  widget: {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden',
  },
  widgetHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', borderBottom: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-subtle)',
  },
  widgetTitle: {
    fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)',
    margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-family-base)',
  },
  hideBtn: {
    background: 'none', border: 'none', fontSize: '18px',
    color: 'var(--color-text-placeholder)', cursor: 'pointer', padding: '0 2px', lineHeight: 1,
  },
  widgetBody: { padding: '14px 16px' },
  widgetLoading: { fontSize: '13px', color: 'var(--color-text-muted)', padding: '8px 0', fontFamily: 'var(--font-family-base)' },
  widgetEmpty: { fontSize: '13px', color: 'var(--color-text-muted)', padding: '8px 0', fontFamily: 'var(--font-family-base)' },
  patientList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  patientRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '7px 8px', backgroundColor: 'transparent', border: 'none',
    borderRadius: 'var(--radius-sm)', cursor: 'pointer', width: '100%', textAlign: 'left',
  },
  patientRowLeft: { display: 'flex', flexDirection: 'column', gap: '1px' },
  patientName: { fontSize: '13px', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  patientMrn: { fontSize: '11px', color: 'var(--color-text-placeholder)', fontFamily: 'var(--font-family-base)' },
  timeAgo: { fontSize: '11px', color: 'var(--color-text-placeholder)', flexShrink: 0, fontFamily: 'var(--font-family-base)' },
  countSummary: {
    textAlign: 'center', padding: '8px 0 12px',
    borderBottom: '1px solid var(--color-border)', marginBottom: '10px',
  },
  totalCount: { fontSize: '36px', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', lineHeight: 1, fontFamily: 'var(--font-family-base)' },
  totalLabel: {
    fontSize: '11px', color: 'var(--color-text-muted)',
    marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'var(--font-family-base)',
  },
  countRows: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '4px' },
  countRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  countLabel: { fontSize: '13px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  countValue: { fontSize: '13px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  dxDistList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  dxDistRow: { display: 'flex', flexDirection: 'column', gap: '3px' },
  dxDistInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  dxDistName: {
    fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)',
    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px',
    fontFamily: 'var(--font-family-base)',
  },
  dxDistCode: { fontSize: '10px', color: 'var(--color-text-placeholder)', flexShrink: 0, fontFamily: 'var(--font-family-base)' },
  dxDistRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  dxBar: {
    flex: 1, height: '4px', backgroundColor: 'var(--color-bg-subtle)',
    borderRadius: '2px', overflow: 'hidden', minWidth: '60px',
  },
  dxBarFill: {
    height: '100%', backgroundColor: 'var(--color-primary)',
    borderRadius: '2px', transition: 'width 0.3s ease',
  },
  dxDistCount: {
    fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)',
    flexShrink: 0, minWidth: '60px', textAlign: 'right', fontFamily: 'var(--font-family-base)',
  },
  dxDistPercent: { fontWeight: 'var(--font-weight-regular)', color: 'var(--color-text-muted)' },
  dxDistTotal: {
    fontSize: '11px', color: 'var(--color-text-muted)',
    marginTop: '10px', paddingTop: '8px', borderTop: '1px solid var(--color-border)',
    fontFamily: 'var(--font-family-base)',
  },
  placeholderBody: { padding: '12px 0' },
  placeholderStage: { fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-primary)', marginBottom: '4px', fontFamily: 'var(--font-family-base)' },
  placeholderDesc: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', lineHeight: '1.5', fontFamily: 'var(--font-family-base)' },
  shortcutList: { display: 'flex', flexDirection: 'column', gap: '6px' },
};